import { z } from 'zod';

export const UI_LOCALE_CODES = [
  'en',
  'es',
  'fr',
  'it',
  'ja',
  'zh',
  'ar',
  'th',
  'de',
  'pt',
  'ko',
  'vi',
  'tr',
  'pl',
  'nl',
  'hi',
  'el',
  'id',
  'he',
  'sw',
  'tl',
] as const;

export const uiLocaleSchema = z.enum(UI_LOCALE_CODES);
export type UiLocale = z.infer<typeof uiLocaleSchema>;

declare const contentLocaleBrand: unique symbol;
export type ContentLocale = string & { readonly [contentLocaleBrand]: true };

function canonicalLocale(value: string): ContentLocale {
  const canonical = Intl.getCanonicalLocales(value)[0];
  if (!canonical) throw new RangeError('Locale is empty');
  return canonical as ContentLocale;
}

export const contentLocaleSchema = z
  .string()
  .trim()
  .min(2)
  .max(35)
  .transform((value, context): ContentLocale => {
    try {
      return canonicalLocale(value);
    } catch {
      context.addIssue({ code: 'custom', message: 'Invalid BCP-47 locale tag' });
      return z.NEVER;
    }
  });

export type LocalizationDomain = 'ui' | 'marketing' | 'event_content';

export function normalizeContentLocale(input: string): ContentLocale {
  return contentLocaleSchema.parse(input);
}

export function baseLanguage(input: string): string {
  try {
    return canonicalLocale(input.trim()).split('-')[0].toLowerCase();
  } catch {
    return input.trim().toLowerCase().split('-')[0];
  }
}

const RTL_BASE_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLocale(input: string): boolean {
  return RTL_BASE_LANGUAGES.has(baseLanguage(input));
}
